import { Fragment, useEffect, useState } from 'react';

import { availabilityApi, type MyAvailability } from '../services/api';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface DayInput {
  on: boolean;
  start: string;
  end: string;
  max: string;
}
const emptyDays = (): Record<number, DayInput> =>
  Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((d) => [d, { on: false, start: '', end: '', max: '' }]));

interface Win {
  day_of_week: number;
  start_time?: string | null;
  end_time?: string | null;
  max_hours?: number | string | null;
}
const daysFromWindows = (windows: Win[]): Record<number, DayInput> => {
  const d = emptyDays();
  for (const w of windows || []) {
    if (d[w.day_of_week]) {
      d[w.day_of_week] = {
        on: true,
        start: w.start_time || '',
        end: w.end_time || '',
        max: w.max_hours != null ? String(w.max_hours) : '',
      };
    }
  }
  return d;
};

/**
 * #2975 — rep availability + PTO on the PWA profile. Thin UI over the shared
 * /api/associates/availability/ API (same endpoints the Django web profile
 * uses). Reps submit requests; a DM approves before anything takes effect.
 */
export default function AvailabilitySection() {
  const [data, setData] = useState<MyAvailability | null>(null);
  const [noAssociate, setNoAssociate] = useState(false);
  const [pto, setPto] = useState({ start: '', end: '', reason: '' });
  const [days, setDays] = useState<Record<number, DayInput>>(emptyDays());
  const [weekly, setWeekly] = useState({ min: '', max: '' });
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const d = await availabilityApi.getMine();
      setData(d);
      // Pre-load the editable form: from the pending request if one exists,
      // else seeded from the live approved availability (edit-from-current).
      const windows = (d.pending_availability?.payload?.windows as Win[] | undefined) ?? (d.availability as Win[]);
      setDays(daysFromWindows(windows));
      // Weekly hour bounds: pre-load from the pending request if it carried them,
      // else from the live associate record.
      const p = (d.pending_availability?.payload ?? {}) as { weekly_min_hours?: unknown; weekly_max_hours?: unknown };
      const pMin = p.weekly_min_hours ?? d.weekly_min_hours;
      const pMax = p.weekly_max_hours ?? d.weekly_max_hours;
      setWeekly({ min: pMin != null ? String(pMin) : '', max: pMax != null ? String(pMax) : '' });
    } catch (e) {
      if (e instanceof Error && e.message.includes('404')) setNoAssociate(true);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async (body: { request_type: 'availability' | 'time_off'; payload: unknown }, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await availabilityApi.createRequest(body);
      setMsg({ text: ok, err: false });
      await load();
      return true;
    } catch {
      setMsg({ text: 'Could not submit request.', err: true });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const requestPto = () => {
    void submit(
      { request_type: 'time_off', payload: { start_date: pto.start, end_date: pto.end, reason: pto.reason } },
      'Time-off request submitted for approval.'
    ).then((ok) => ok && setPto({ start: '', end: '', reason: '' }));
  };

  const requestAvailability = () => {
    const windows = Object.entries(days)
      .filter(([, v]) => v.on)
      .map(([dow, v]) => ({
        day_of_week: parseInt(dow),
        start_time: v.start || undefined,
        end_time: v.end || undefined,
        max_hours: v.max ? parseFloat(v.max) : undefined,
      }));
    const payload: Record<string, unknown> = { windows };
    // Only send weekly bounds when typed, so a bare change never clears them.
    if (weekly.min !== '') payload.weekly_min_hours = parseFloat(weekly.min);
    if (weekly.max !== '') payload.weekly_max_hours = parseFloat(weekly.max);
    void submit({ request_type: 'availability', payload }, 'Availability request submitted for approval.').then(
      (ok) => ok && setDays(emptyDays())
    );
  };

  const withdraw = async (id: number) => {
    if (!window.confirm('Withdraw this pending request?')) return;
    setBusy(true);
    setMsg(null);
    try {
      await availabilityApi.withdrawRequest(id);
      setMsg({ text: 'Request withdrawn.', err: false });
      await load();
    } catch {
      setMsg({ text: 'Could not withdraw request.', err: true });
    } finally {
      setBusy(false);
    }
  };

  const setDay = (dow: number, patch: Partial<DayInput>) =>
    setDays((prev) => ({ ...prev, [dow]: { ...prev[dow], ...patch } }));
  const anyDayOn = Object.values(days).some((d) => d.on);

  // The live approved availability for a weekday, as fixed text ("—" if none).
  const approvedLabel = (dow: number): string => {
    const ws = (data?.availability || []).filter((w) => w.day_of_week === dow);
    if (!ws.length) return '—';
    return ws
      .map((w) => (w.start_time ? `${w.start_time}–${w.end_time}` : 'all day') + (w.max_hours ? ` · ${w.max_hours}h` : ''))
      .join(', ');
  };

  if (noAssociate) return null;

  return (
    <section className="rounded-xl bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left"
      >
        <h2 className="text-lg font-semibold text-slate-800">My Availability</h2>
        <svg
          className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!open ? null : (
        <>
      <p className="mb-3 mt-2 text-xs text-slate-500">Requests are reviewed by your manager before they take effect.</p>

      <div className="mb-4">
        <h3 className="mb-1 text-sm font-medium text-slate-700">Days off</h3>
        {!data?.time_off.length && <p className="text-xs text-slate-400">None</p>}
        <ul className="text-sm text-slate-600">
          {data?.time_off.map((t) => (
            <li key={t.id}>
              {t.start_date} → {t.end_date}
              {t.reason ? ` (${t.reason})` : ''}
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-3 rounded-lg border border-slate-200 p-3">
        <h3 className="mb-2 text-sm font-medium text-slate-700">Request days off</h3>
        <p className="mb-2 text-xs text-slate-500">Mark dates you can&apos;t work so you won&apos;t be scheduled (once approved).</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500">
            From
            <input type="date" value={pto.start} onChange={(e) => setPto({ ...pto, start: e.target.value })}
              className="block rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-slate-500">
            To
            <input type="date" value={pto.end} onChange={(e) => setPto({ ...pto, end: e.target.value })}
              className="block rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-slate-500">
            Reason (optional)
            <input type="text" value={pto.reason} onChange={(e) => setPto({ ...pto, reason: e.target.value })}
              className="block rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <button type="button" disabled={busy || !pto.start || !pto.end} onClick={requestPto}
            className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
            Submit request
          </button>
        </div>
      </div>

      <div className="mb-3 rounded-lg border border-slate-200 p-3">
        <h3 className="mb-2 text-sm font-medium text-slate-700">My requested change</h3>
        {data?.pending_availability ? (
          <p className="mb-2 text-xs text-amber-600">
            Pending approval — submitted {(data.pending_availability.created_at || '').slice(0, 10)}. Edit and resubmit to replace it.
          </p>
        ) : (
          <p className="mb-2 text-xs text-slate-500">
            No pending change. Tick each day you can work; optionally set hours + a daily max (blank = all day). Replaces
            your active availability once approved.
          </p>
        )}
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-500">
            Weekly min hours
            <input type="number" min="0" step="0.5" value={weekly.min}
              onChange={(e) => setWeekly({ ...weekly, min: e.target.value })}
              className="block w-24 rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-slate-500">
            Weekly max hours
            <input type="number" min="0" step="0.5" value={weekly.max}
              onChange={(e) => setWeekly({ ...weekly, max: e.target.value })}
              className="block w-24 rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          {(data?.weekly_min_hours || data?.weekly_max_hours) && (
            <span className="text-xs text-slate-400">
              Active: {data?.weekly_min_hours ?? '–'} / {data?.weekly_max_hours ?? '–'}
            </span>
          )}
        </div>
        <div className="grid grid-cols-[3.5rem_1fr_auto] items-center gap-x-3 gap-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Day</div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">My requested change</div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Active (approved)</div>
          {DAY_NAMES.map((name, dow) => (
            <Fragment key={dow}>
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={days[dow].on} onChange={(e) => setDay(dow, { on: e.target.checked })} />
                {name}
              </label>
              <span className="flex flex-wrap items-center gap-1">
                {days[dow].on ? (
                  <>
                    <input type="time" value={days[dow].start} onChange={(e) => setDay(dow, { start: e.target.value })}
                      className="rounded border border-slate-300 px-1 py-0.5 text-xs" />
                    <span className="text-slate-400">–</span>
                    <input type="time" value={days[dow].end} onChange={(e) => setDay(dow, { end: e.target.value })}
                      className="rounded border border-slate-300 px-1 py-0.5 text-xs" />
                    <input type="number" min="0" step="0.5" placeholder="max h" value={days[dow].max}
                      onChange={(e) => setDay(dow, { max: e.target.value })}
                      className="w-16 rounded border border-slate-300 px-1 py-0.5 text-xs" />
                  </>
                ) : (
                  <span className="text-xs text-slate-300">not working</span>
                )}
              </span>
              <span className="whitespace-nowrap text-xs text-slate-500">{approvedLabel(dow)}</span>
            </Fragment>
          ))}
        </div>
        <button type="button" disabled={busy || !anyDayOn} onClick={requestAvailability}
          className="mt-2 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
          Submit request
        </button>
      </div>

      <h3 className="mb-1 text-sm font-medium text-slate-700">My requests</h3>
      {!data?.requests.length && <p className="text-xs text-slate-400">None yet.</p>}
      <ul className="text-sm text-slate-600">
        {data?.requests.map((r) => (
          <li key={r.id} className="flex items-center gap-2">
            <span>{r.request_type === 'time_off' ? 'Days off' : 'Availability'} —</span>
            <span
              className={
                r.status === 'approved' ? 'text-green-700' : r.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
              }
            >
              {r.status}
            </span>
            <span className="text-xs text-slate-400">{(r.created_at || '').slice(0, 10)}</span>
            {r.status === 'pending' && (
              <button type="button" disabled={busy} onClick={() => void withdraw(r.id)}
                className="text-xs font-medium text-red-600 disabled:opacity-50">
                Withdraw
              </button>
            )}
          </li>
        ))}
      </ul>

      {msg && <p className={`mt-2 text-sm ${msg.err ? 'text-red-600' : 'text-green-700'}`}>{msg.text}</p>}
        </>
      )}
    </section>
  );
}
